// Setting one resource limit (D83): the default, a role's or a person's. CPUs and whole GiB,
// between the floor every isolation can be created at and the host's size; the API's refusal
// (422) is shown in the dialog. Adding a person's limit first picks the person.
import { X } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { type FormEvent, useId, useState } from "react";
import { ActionIcon, Button, Modal } from "rizzui";

import { usersQuery } from "../../auth/admin";
import { useT } from "../../i18n";
import { ErrorState } from "../../ui/states";
import { fromGib, type Size, toGib } from "./api";
import { Stepper } from "./SizeFields";

const selectClass =
  "w-full rounded-md border border-gray-300 bg-transparent px-3 py-2 text-sm text-gray-900 focus:border-primary focus:outline-none";

export interface LimitEdit {
  title: string;
  initial: Size;
  /** Set when the dialog adds a person's limit: those who already have one are left out. */
  pickUser?: { exclude: string[] };
}

function PersonSelect({ exclude, value, onChange }: { exclude: string[]; value: string; onChange: (id: string) => void }) {
  const t = useT().limits.dialog;
  const id = useId();
  const users = useQuery(usersQuery());
  if (users.isError) return <ErrorState title={t.usersFailed} message={users.error.message} />;
  const choices = (users.data ?? []).filter((u) => !exclude.includes(u.id));
  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-sm font-medium text-gray-900">
        {t.person}
      </label>
      <select id={id} className={selectClass} value={value} onChange={(e) => onChange(e.target.value)} required>
        <option value="">{t.choosePerson}</option>
        {choices.map((u) => (
          <option key={u.id} value={u.id}>
            {u.display_name ? `${u.display_name} (${u.email})` : u.email}
          </option>
        ))}
      </select>
    </div>
  );
}

export function LimitDialog({
  edit,
  min,
  max,
  onClose,
  onSave,
}: {
  edit: LimitEdit;
  min: Size;
  max: Size;
  onClose: () => void;
  /** Resolves when saved; a rejection's message is shown in the dialog. */
  onSave: (size: Size, userId?: string) => Promise<void>;
}) {
  const { common, limits } = useT();
  const t = limits.dialog;
  const [cpus, setCpus] = useState(edit.initial.cpus);
  const [gib, setGib] = useState(toGib(edit.initial.memory_bytes));
  const [userId, setUserId] = useState("");
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (edit.pickUser && !userId) return;
    setBusy(true);
    setError(undefined);
    try {
      await onSave({ cpus, memory_bytes: fromGib(gib) }, edit.pickUser ? userId : undefined);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal isOpen onClose={onClose} containerClassName="dark:bg-gray-100">
      <form className="p-6" aria-label={edit.title} onSubmit={(e) => void submit(e)}>
        <div className="mb-5 flex items-center justify-between">
          <h2 className="text-lg">{edit.title}</h2>
          <ActionIcon size="sm" variant="text" onClick={onClose} aria-label={common.actions.close}>
            <X weight="bold" className="h-4 w-4" />
          </ActionIcon>
        </div>
        <div className="space-y-4">
          {edit.pickUser ? <PersonSelect exclude={edit.pickUser.exclude} value={userId} onChange={setUserId} /> : null}
          <Stepper label={limits.cpus} value={cpus} min={min.cpus} max={max.cpus} onChange={setCpus} fewer={t.fewer} more={t.more} />
          <Stepper
            label={limits.memory}
            value={gib}
            min={toGib(min.memory_bytes)}
            max={toGib(max.memory_bytes)}
            onChange={setGib}
            fewer={t.fewerMemory}
            more={t.moreMemory}
          />
          <p className="text-xs text-gray-500">{t.lowered}</p>
        </div>
        {error ? <ErrorState className="mt-4" title={t.notSaved} message={error} /> : null}
        <div className="mt-6 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>
            {common.actions.cancel}
          </Button>
          <Button type="submit" isLoading={busy} disabled={edit.pickUser !== undefined && !userId}>
            {common.actions.save}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
